/**
 * Maps Pacific deployment environment slugs to banner label + colour tier.
 *
 * Colour tiers follow Christmas banner colours (DS semantic tokens in CSS):
 * - production → blue
 * - test (Sandbox, UAT) → orange — Sandbox is orange because it connects to Christmas UAT
 * - dev (Dev, Preview, Local) → green
 * - unknown → fail-safe warning styling
 */

export type DeploymentColourTier = "production" | "test" | "dev" | "unknown";

export interface DeploymentBannerInfo {
  /** Raw slug after trim + lowercase (empty string when missing). */
  slug: string;
  /** Plain-language label shown in the banner. */
  label: string;
  colourTier: DeploymentColourTier;
}

const KNOWN: Record<
  string,
  { label: string; colourTier: Exclude<DeploymentColourTier, "unknown"> }
> = {
  production: { label: "Production", colourTier: "production" },
  prod: { label: "Production", colourTier: "production" },
  sandbox: { label: "Sandbox", colourTier: "test" },
  uat: { label: "UAT", colourTier: "test" },
  preview: { label: "Preview", colourTier: "dev" },
  local: { label: "Local", colourTier: "dev" },
  development: { label: "Local", colourTier: "dev" },
  dev: { label: "Dev", colourTier: "dev" },
};

/**
 * Resolve banner copy and colour tier from a deployment-environment config value.
 * Empty / unrecognised values use the unknown fail-safe (never silent).
 */
export function resolveDeploymentBanner(
  raw: string | null | undefined,
): DeploymentBannerInfo {
  const slug = (raw ?? "").trim().toLowerCase();
  if (!slug) {
    return {
      slug: "",
      label: "Unknown environment",
      colourTier: "unknown",
    };
  }

  // Own-property check: `KNOWN[slug]` alone would also match inherited
  // Object.prototype keys (e.g. constructor, toString) and spread undefined fields.
  if (Object.hasOwn(KNOWN, slug)) {
    return { slug, ...KNOWN[slug] };
  }

  return {
    slug,
    label: "Unknown environment",
    colourTier: "unknown",
  };
}
